import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBuilding,
  FaDatabase,
  FaGlobe,
  FaListUl,
  FaUsers,
} from 'react-icons/fa';
import { HomeDirectionIcon } from '../components/HomeDirectionIcon.tsx';
import { HomeWhySection } from '../components/HomeWhySection.tsx';
import type { HomeDirectionId } from '../config/homeDirectionIcons.ts';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { HOME_DIRECTION_TOPICS } from '../utils/problemUrls.ts';
import { useSeoMeta, defaultSiteDescription } from '../hooks/useSeoMeta.ts';
import './css/HomePage.css';

type StatItem = {
  icon: ReactNode;
  value: string;
  label: string;
};

type DirectionItem = {
  id: HomeDirectionId;
  title: string;
  description: string;
  tone: 'teal' | 'gold';
};

function HeroGlobe() {
  return (
    <div className="home-hero-visual" aria-hidden>
      <img
        src="/home/hero-planet.png"
        alt=""
        className="home-hero-image"
        loading="eager"
        decoding="async"
      />
    </div>
  );
}

function HomePage() {
  const { t, currentLanguage } = useTranslation();

  useSeoMeta({
    title:
      currentLanguage === 'ru'
        ? 'Sciplex — задачи по программированию, SQL и алгоритмам онлайн'
        : 'Sciplex — programming, SQL and algorithm practice online',
    description: defaultSiteDescription(currentLanguage),
    canonicalPath: '/',
  });

  const heroFeatures = [
    t('homePage.heroFeature1'),
    t('homePage.heroFeature2'),
    t('homePage.heroFeature3'),
    t('homePage.heroFeature4'),
  ];

  const stats: StatItem[] = [
    { icon: <FaListUl />, value: '25 000+', label: t('homePage.statTasks') },
    { icon: <FaDatabase />, value: '120+', label: t('homePage.statTracks') },
    { icon: <FaUsers />, value: '180K+', label: t('homePage.statUsers') },
    { icon: <FaBuilding />, value: '950+', label: t('homePage.statOrgs') },
    { icon: <FaGlobe />, value: '50+', label: t('homePage.statCountries') },
  ];

  const directions: DirectionItem[] = [
    {
      id: 'algorithms',
      title: t('homePage.dirAlgorithms'),
      description: t('homePage.dirAlgorithmsDesc'),
      tone: 'teal',
    },
    {
      id: 'sql',
      title: t('homePage.dirSql'),
      description: t('homePage.dirSqlDesc'),
      tone: 'gold',
    },
    {
      id: 'math',
      title: t('homePage.dirMath'),
      description: t('homePage.dirMathDesc'),
      tone: 'teal',
    },
    {
      id: 'physics',
      title: t('homePage.dirPhysics'),
      description: t('homePage.dirPhysicsDesc'),
      tone: 'gold',
    },
    {
      id: 'ml',
      title: t('homePage.dirMl'),
      description: t('homePage.dirMlDesc'),
      tone: 'teal',
    },
    {
      id: 'pandas',
      title: t('homePage.dirPandas'),
      description: t('homePage.dirPandasDesc'),
      tone: 'gold',
    },
  ];

  return (
    <div className="home-page">
      <section className="home-hero">
        <div className="home-container home-hero-grid">
          <div className="home-hero-copy">
            <h1 className="home-hero-title">
              <span className="home-hero-title-line">{t('homePage.heroTitlePrefix')}</span>
              <span className="home-hero-title-line home-hero-title-line--accent">
                {t('homePage.heroTitleHighlight')}
              </span>
              <span className="home-hero-title-line">{t('homePage.heroTitleSuffix')}</span>
            </h1>
            <p className="home-hero-subtitle">{t('homePage.heroSubtitle')}</p>
            <div className="home-hero-actions">
              <Link to="/register" className="home-btn home-btn--primary">
                {t('homePage.heroCtaPrimary')}
                <FaArrowRight aria-hidden />
              </Link>
              <Link to="/problems" className="home-btn home-btn--ghost">
                {t('homePage.heroCtaSecondary')}
              </Link>
            </div>
            <ul className="home-hero-features">
              {heroFeatures.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          </div>
          <HeroGlobe />
        </div>
      </section>

      <section className="home-stats" aria-label={t('homePage.statsAria')}>
        <div className="home-container home-stats-grid">
          {stats.map((item) => (
            <article key={item.label} className="home-stat-card">
              <span className="home-stat-icon">{item.icon}</span>
              <div className="home-stat-value">{item.value}</div>
              <div className="home-stat-label">{item.label}</div>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section home-section--directions" id="directions">
        <div className="home-container">
          <div className="home-section-head">
            <h2 className="home-section-title">{t('homePage.directionsTitle')}</h2>
            <Link to="/problems" className="home-section-link home-section-link--muted">
              {t('homePage.directionsAll')}
              <FaArrowRight aria-hidden />
            </Link>
          </div>
          <div className="home-directions-scroll">
            <div className="home-directions-grid">
              {directions.map((dir) => (
                <Link
                  key={dir.id}
                  to={`/problems/${HOME_DIRECTION_TOPICS[dir.id]?.topicSlug || dir.id}`}
                  className={`home-direction-card home-direction-card--${dir.tone}`}
                >
                  <HomeDirectionIcon id={dir.id} tone={dir.tone} />
                  <h3 className="home-direction-title">{dir.title}</h3>
                  <p className="home-direction-desc">{dir.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <HomeWhySection />
    </div>
  );
}

export default HomePage;
